import React from "react";
import {
  FiAlertTriangle,
  FiArrowRight,
  FiBookOpen,
  FiMessageCircle,
  FiPlayCircle,
  FiSend,
  FiVolume2,
} from "react-icons/fi";
import { DashboardSidebar, DashboardTopbar } from "./page.tsx";

type DashboardCommunityProps = {
  preview?: boolean;
};

const announcements = [
  {
    title: "Nova sessão de análise",
    body: "Resumo das melhores oportunidades M5 para os principais pares forex.",
    time: "há 1 dia",
  },
  {
    title: "Regras de entrada atualizadas",
    body: "Os sinais agora priorizam setups com maior consistência durante a sessão europeia.",
    time: "há 3 dias",
  },
  {
    title: "Lembrete de gestão de risco",
    body: "Define sempre um limite diário e evita operar fora do teu plano.",
    time: "há 5 dias",
  },
];

const lessons = [
  { title: "Como interpretar um sinal M5", level: "Iniciante", minutes: 6 },
  { title: "Confiança da IA como probabilidade", level: "Iniciante", minutes: 8 },
  { title: "Gestão de risco por sessão", level: "Intermédio", minutes: 12 },
  { title: "CALL e PUT com disciplina", level: "Avançado", minutes: 15 },
];

export default function DashboardCommunity({
  preview = false,
}: DashboardCommunityProps) {
  return (
    <div className="min-h-screen bg-[#f4f7f1] text-[#0d2a1f]">
      <DashboardSidebar active="Comunidade" preview={preview} />

      <main className="min-h-screen xl:pl-[270px]">
        <DashboardTopbar />

        <div className="mx-auto max-w-6xl px-5 py-7 xl:px-6">
          <div className="mb-6">
            <h1 className="text-3xl font-black tracking-[-0.04em] text-[#0d2a1f]">
              Comunidade
            </h1>
            <p className="mt-1.5 text-sm font-medium text-[#73827b]">
              Liga-te a outros traders, anúncios e conteúdo educativo.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ChannelCard
              icon={FiSend}
              title="Canal de Telegram"
              description="Sinais e avisos em tempo real."
              buttonText="Abrir Telegram"
            />
            <ChannelCard
              icon={FiMessageCircle}
              title="Canal do Whatsapp"
              description="Comunidade, avisos e suporte rápido."
              buttonText="Entrar no Whatsapp"
            />
          </div>

          <section className="mt-5 rounded-[20px] border border-[#dfe8e1] bg-white p-4 shadow-[0_12px_30px_rgba(20,44,33,0.07)]">
            <h2 className="flex items-center gap-2 text-base font-black text-[#0d2a1f]">
              <FiVolume2 className="h-5 w-5" />
              Anúncios
            </h2>

            <div className="mt-3 divide-y divide-[#dfe8e1]">
              {announcements.map((announcement) => (
                <div
                  key={announcement.title}
                  className="flex items-start justify-between gap-4 py-3 first:pt-1 last:pb-0"
                >
                  <div>
                    <p className="text-sm font-black text-[#0d2a1f]">
                      {announcement.title}
                    </p>
                    <p className="mt-0.5 text-sm font-medium leading-relaxed text-[#73827b]">
                      {announcement.body}
                    </p>
                  </div>
                  <span className="shrink-0 text-xs font-bold text-[#83928b]">
                    {announcement.time}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-5 rounded-[20px] border border-[#dfe8e1] bg-white p-4 shadow-[0_12px_30px_rgba(20,44,33,0.07)]">
            <h2 className="flex items-center gap-2 text-base font-black text-[#0d2a1f]">
              <FiBookOpen className="h-5 w-5" />
              Conteúdo educativo
            </h2>

            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              {lessons.map((lesson) => (
                <button
                  key={lesson.title}
                  type="button"
                  className="group flex h-[72px] items-center gap-3 rounded-2xl border border-[#dfe8e1] bg-[#f4f7f1] px-4 text-left transition hover:border-[#bfe8d3] hover:bg-[#edf7ef]"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#087a45] shadow-[0_8px_18px_rgba(20,44,33,0.06)]">
                    <FiPlayCircle className="h-5 w-5 transition group-hover:scale-105" />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-black text-[#0d2a1f]">
                      {lesson.title}
                    </span>
                    <span className="mt-0.5 block text-xs font-semibold text-[#73827b]">
                      {lesson.level} · {lesson.minutes} min
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="border-t border-[#dfe8e1] bg-[#f4f7f1]/95 px-5 py-4 xl:px-8">
          <div className="mx-auto flex max-w-6xl flex-col gap-3 text-xs font-semibold leading-relaxed text-[#73827b] md:flex-row md:items-center md:justify-between">
            <p className="flex items-start gap-2">
              <FiAlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[#f08700]" />
              As opções binárias envolvem risco elevado. Os sinais são informativos e não constituem aconselhamento financeiro.
            </p>
            <p className="font-black text-[#73827b]">
              Restrição 18+. Dados de demonstração fictícios.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

function ChannelCard({
  icon: Icon,
  title,
  description,
  buttonText,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  buttonText: string;
}) {
  return (
    <section className="rounded-[20px] border border-[#dfe8e1] bg-white p-4 shadow-[0_12px_30px_rgba(20,44,33,0.07)]">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#dff6ea] text-[#087a45]">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <h2 className="text-base font-black text-[#0d2a1f]">{title}</h2>
          <p className="mt-0.5 text-sm font-medium text-[#73827b]">
            {description}
          </p>
        </div>
      </div>

      <button
        type="button"
        className="mt-4 flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#20c77a] text-sm font-black text-[#03120e] shadow-[0_10px_24px_rgba(32,199,122,0.22)] transition hover:bg-[#18b56d] hover:text-[#03120e]"
      >
        {buttonText}
        <FiArrowRight className="h-4 w-4" />
      </button>
    </section>
  );
}
